/* Identifica um item de uma lista do catálogo por uma descrição livre.
   Mesmo algoritmo de app/core/referencias.py no backend: igualdade sem acentos e
   maiúsculas e, se não houver, semelhança pelas palavras (palavras raras pesam mais). */

export interface ItemReferencia {
  id: string | number;
  nome: string;
}

const PONTUACAO_MINIMA = 0.6;
const MARGEM_MINIMA = 0.05;
const IGNORADAS = new Set(['a', 'as', 'o', 'os', 'e', 'de', 'da', 'das', 'do', 'dos', 'em', 'na', 'nas', 'no', 'nos', 'com', 'para', 'por', 'ou', 'um', 'uma', 'tipo']);
const NOMES_IGNORADOS = new Set(['duplicado']);

export const textoRef = (v: unknown): string =>
  String(v ?? '').normalize('NFKD').replace(/[̀-ͯ]/g, '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();

/** Radical: sem plural e com 6 letras ("secas"/"seca", "incineracao"/"incinerador"). */
export const palavrasRef = (texto: string): string[] =>
  texto.split(' ').filter((p) => p && !IGNORADAS.has(p) && !/^\d+$/.test(p))
    .map((p) => (p.length > 3 && p.endsWith('s') ? p.slice(0, -1) : p).slice(0, 6));

/** Razão de semelhança como difflib.SequenceMatcher.ratio (blocos comuns mais longos). */
function parecido(a: string, b: string): number {
  if (!a.length && !b.length) return 1;
  const blocos = (x: string, y: string): number => {
    if (!x || !y) return 0;
    let melhor = 0, ix = 0, iy = 0;
    const tab: number[] = new Array(y.length + 1).fill(0);
    for (let i = 1; i <= x.length; i++) {
      let anterior = 0;
      for (let j = 1; j <= y.length; j++) {
        const atual = tab[j];
        tab[j] = x[i - 1] === y[j - 1] ? anterior + 1 : 0;
        if (tab[j] > melhor) { melhor = tab[j]; ix = i; iy = j; }
        anterior = atual;
      }
    }
    if (!melhor) return 0;
    return melhor + blocos(x.slice(0, ix - melhor), y.slice(0, iy - melhor)) + blocos(x.slice(ix), y.slice(iy));
  };
  return (2 * blocos(a, b)) / (a.length + b.length);
}

export interface Candidato<T> {
  pontos: number;
  item: T;
}

/** Itens do mais para o menos parecido; igualdade exata vale 1. */
export function candidatos<T extends ItemReferencia>(descricao: unknown, itens: T[]): Candidato<T>[] {
  const texto = textoRef(descricao);
  const palavras = palavrasRef(texto);
  const validos = itens.map((item) => ({ item, nome: textoRef(item.nome) })).filter((x) => x.nome && !NOMES_IGNORADOS.has(x.nome));
  const exato = validos.find((x) => x.nome === texto);
  if (exato) return [{ pontos: 1, item: exato.item }];

  const frequencia = new Map<string, number>();
  validos.forEach((x) => new Set(palavrasRef(x.nome)).forEach((p) => frequencia.set(p, (frequencia.get(p) || 0) + 1)));
  const total = Math.max(validos.length, 1);
  const peso = new Map([...frequencia].map(([p, f]) => [p, Math.log(1 + total / f)]));

  return validos.map(({ item, nome }) => {
    const doNome = new Set(palavrasRef(nome));
    const conhecidas = new Set(palavras.filter((p) => peso.has(p)));
    if (!conhecidas.size || !doNome.size) return { pontos: 0, item };
    const comuns = [...conhecidas].filter((p) => doNome.has(p));
    const soma = (ps: Iterable<string>) => [...ps].reduce((s, p) => s + (peso.get(p) || 0), 0);
    const pontos = 0.6 * (soma(comuns) / soma(conhecidas)) + 0.15 * (comuns.length / doNome.size) + 0.25 * parecido(texto, nome);
    return { pontos, item };
  }).sort((a, b) => b.pontos - a.pontos);
}

export type ResultadoRef<T> =
  | { ok: true; item: T; exato: boolean }
  | { ok: false; sugestoes: T[] };

/** Item correspondente; com dúvida (pouco parecido ou empate), devolve os mais parecidos. */
export function casarReferencia<T extends ItemReferencia>(descricao: unknown, itens: T[]): ResultadoRef<T> {
  const texto = String(descricao ?? '').trim();
  const porId = itens.find((i) => String(i.id) === texto || String(i.id) === texto.replace(/\.0+$/, '') || String(i.id) === texto.match(/^(\d+)\s*[-–:|]\s*\S/)?.[1]);
  if (porId) return { ok: true, item: porId, exato: true };
  const lista = candidatos(texto, itens);
  if (lista.length) {
    const [melhor, segundo] = lista;
    if (melhor.pontos === 1) return { ok: true, item: melhor.item, exato: true };
    if (melhor.pontos >= PONTUACAO_MINIMA && (melhor.pontos - (segundo?.pontos || 0) >= MARGEM_MINIMA || melhor.pontos >= 0.9)) {
      return { ok: true, item: melhor.item, exato: false };
    }
  }
  return { ok: false, sugestoes: lista.filter((c) => c.pontos > 0).slice(0, 3).map((c) => c.item) };
}
